import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Trophy, Users, Calendar, Shield, BarChart3, Search,
    ChevronRight, LogOut, User, Settings, Trash2, Check,
    X, School, Activity, TrendingUp, Eye, UserCheck, UserX,
    Plus, Edit, Download, Upload, FileSpreadsheet, Save, RefreshCw,
    Clock, UserPlus, UserCog, LogIn, DollarSign, GitBranch, Bell, Video,
    Radio, ShoppingBag, Award, Crown, Building2, Brain, Sparkles, Briefcase
} from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';
import ManualLocationEntry from '../components/ManualLocationEntry';

// Import modular admin components
import {
    OverviewTab,
    AthletesTab,
    TournamentsTab,
    UsersTab,
    SchoolsTab,
    ImportExportTab,
    SettingsTab,
    RegistrationsTab,
    AccountingTab,
    MatchesTab,
    BracketTab,
    NotificationsTab,
    VideoAnalysisTab
} from '../components/admin';
import LiveScoringTab from '../components/admin/LiveScoringTab';
import MarketplaceTab from '../components/admin/MarketplaceTab';
import RankingsTab from '../components/admin/RankingsTab';
import SponsorshipTab from '../components/admin/SponsorshipTab';
import HierarchyTab from '../components/admin/HierarchyTab';
import TalentAITab from '../components/admin/TalentAITab';
import ApplicationsTab from '../components/admin/ApplicationsTab';
import CombinedRankingsTab from '../components/admin/CombinedRankingsTab';
import FederationDashboardTab from '../components/admin/FederationDashboardTab';
import LiveStreamingTab from '../components/admin/LiveStreamingTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
    const { user, token, logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [athletes, setAthletes] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [users, setUsers] = useState([]);
    const [schoolRankings, setSchoolRankings] = useState([]);
    const [settings, setSettings] = useState(null);
    const [rbacPermissions, setRbacPermissions] = useState(null);
    
    // Filters
    const [athleteFilter, setAthleteFilter] = useState({ verified: 'all', sport: 'all', school: 'all', search: '' });
    const [tournamentFilter, setTournamentFilter] = useState({ status: 'all', sport: 'all', search: '' });
    const [userFilter, setUserFilter] = useState({ role: 'all', search: '' });

    // Dialogs
    const [showTournamentDialog, setShowTournamentDialog] = useState(false);
    const [showAthleteDialog, setShowAthleteDialog] = useState(false);
    const [showSchoolDialog, setShowSchoolDialog] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showRoleDialog, setShowRoleDialog] = useState(false);
    const [showAdminDialog, setShowAdminDialog] = useState(false);
    
    // Edit states
    const [editingTournament, setEditingTournament] = useState(null);
    const [editingAthlete, setEditingAthlete] = useState(null);
    const [editingSchool, setEditingSchool] = useState(null);
    const [editingUserRole, setEditingUserRole] = useState(null);
    const [importType, setImportType] = useState('athletes');
    
    // New admin form
    const [newAdminForm, setNewAdminForm] = useState({
        full_name: '',
        email: '',
        password: '',
        admin_role: 'admin_team'
    });
    
    // Bulk selection states
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [selectedTournaments, setSelectedTournaments] = useState([]);
    const [selectedSchools, setSelectedSchools] = useState([]);
    const [showBulkRoleDialog, setShowBulkRoleDialog] = useState(false);
    const [showBulkAthleteActionDialog, setShowBulkAthleteActionDialog] = useState(false);
    
    const fileInputRef = useRef(null);

    const sports = [
        'Athletics Track and Field', 'Basketball', 'Hockey', 'Football', 'Volleyball',
        'Rugby', 'Netball', 'Chess', 'Table Tennis', 'Karate', 'Taekwondo',
        'Badminton', 'Swimming', 'Tennis', 'Handball', 'Judo', 'Padel',
        'Squash', 'E-Sports', 'Motor Racing'
    ];

    // Sport-specific disciplines/events
    const sportDisciplines = {
        'Athletics Track and Field': [
            '100 Meters', '200 Meters', '400 Meters', '800 Meters', '1500 Meters', '3000 Meters',
            '400 Meters Relay', '800 Meters Relay', '400 Meters Mixed Relay', '800 Meters Mixed Relay',
            'Long Jump', 'High Jump', 'Triple Jump', 'Shot Put', 'Discus Throw', 'Javelin Throw'
        ],
        'Basketball': [
            '5v5 Full Court', '3v3 Half Court', 'Skills Challenge', 'Free Throw Contest', 'Slam Dunk Contest'
        ],
        'Football': [
            '11-a-side', '7-a-side', '5-a-side (Futsal)', 'Penalty Shootout', 'Skills Challenge'
        ],
        'Swimming': [
            '50m Freestyle', '100m Freestyle', '200m Freestyle', '400m Freestyle',
            '50m Backstroke', '100m Backstroke', '50m Breaststroke', '100m Breaststroke',
            '50m Butterfly', '100m Butterfly', '200m Individual Medley', '4x50m Freestyle Relay',
            '4x50m Medley Relay'
        ],
        'Tennis': [
            'Singles', 'Doubles', 'Mixed Doubles'
        ],
        'Table Tennis': [
            'Singles', 'Doubles', 'Mixed Doubles', 'Team Event'
        ],
        'Badminton': [
            'Singles', 'Doubles', 'Mixed Doubles'
        ],
        'Volleyball': [
            '6v6 Indoor', 'Beach Volleyball (2v2)', '4v4'
        ],
        'Hockey': [
            'Field Hockey 11-a-side', 'Field Hockey 6-a-side', 'Indoor Hockey'
        ],
        'Rugby': [
            'Rugby 15s', 'Rugby 7s', 'Tag Rugby', 'Touch Rugby'
        ],
        'Netball': [
            '7-a-side', '5-a-side', 'Fast5'
        ],
        'Chess': [
            'Classical', 'Rapid', 'Blitz', 'Bullet', 'Team Championship'
        ],
        'Karate': [
            'Kata Individual', 'Kata Team', 'Kumite -55kg', 'Kumite -60kg', 'Kumite -67kg',
            'Kumite -75kg', 'Kumite +75kg', 'Kumite Open Weight'
        ],
        'Taekwondo': [
            'Poomsae Individual', 'Poomsae Team', 'Kyorugi -54kg', 'Kyorugi -58kg', 'Kyorugi -63kg',
            'Kyorugi -68kg', 'Kyorugi -74kg', 'Kyorugi +74kg'
        ],
        'Handball': [
            '7-a-side Indoor', 'Beach Handball'
        ],
        'Judo': [
            '-60kg', '-66kg', '-73kg', '-81kg', '-90kg', '-100kg', '+100kg',
            '-48kg', '-52kg', '-57kg', '-63kg', '-70kg', '-78kg', '+78kg', 'Team Event'
        ],
        'Padel': [
            'Doubles', 'Mixed Doubles'
        ],
        'Squash': [
            'Singles', 'Doubles', 'Mixed Doubles'
        ],
        'E-Sports': [
            'League of Legends', 'Dota 2', 'CS:GO/CS2', 'Valorant', 'Fortnite', 
            'Rocket League', 'FIFA/EA Sports FC', 'Call of Duty', 'Overwatch 2', 
            'PUBG', 'Apex Legends', 'Rainbow Six Siege', 'Street Fighter', 'Tekken',
            'Super Smash Bros', 'Mobile Legends', 'Free Fire'
        ],
        'Motor Racing': [
            'Formula Racing', 'GT/Sports Car', 'Touring Car', 'Karting', 
            'Rally', 'Endurance Racing', 'Drag Racing', 'Motocross', 
            'Superbike', 'Sim Racing'
        ]
    };

    // Team sports - entry fee is per team
    const teamSports = ['Football', 'Volleyball', 'Hockey', 'Rugby', 'Netball', 'Handball', 'Basketball'];
    
    // Check if selected sport is a team sport
    const isTeamSport = (sport) => teamSports.includes(sport);

    // Sport-specific default game settings
    const sportGameSettings = {
        'Football': {
            periodName: 'Half',
            defaultPeriods: 2,
            defaultDuration: 45,
            defaultBreak: 15,
            hasExtraTime: true,
            extraTimePeriods: 2,
            extraTimeDuration: 15,
            hasTimeControl: false
        },
        'Basketball': {
            periodName: 'Quarter',
            defaultPeriods: 4,
            defaultDuration: 10,
            defaultBreak: 15,
            hasExtraTime: true,
            extraTimePeriods: 1,
            extraTimeDuration: 5,
            hasTimeControl: false
        },
        'Hockey': {
            periodName: 'Half',
            defaultPeriods: 2,
            defaultDuration: 35,
            defaultBreak: 10,
            hasExtraTime: true,
            extraTimePeriods: 2,
            extraTimeDuration: 7.5,
            hasTimeControl: false
        },
        'Rugby': {
            periodName: 'Half',
            defaultPeriods: 2,
            defaultDuration: 40,
            defaultBreak: 10,
            hasExtraTime: true,
            extraTimePeriods: 2,
            extraTimeDuration: 10,
            hasTimeControl: false
        },
        'Volleyball': {
            periodName: 'Set',
            defaultPeriods: 5,
            defaultDuration: 0, // Points-based, not time
            defaultBreak: 3,
            hasExtraTime: false,
            pointsToWin: 25,
            hasTimeControl: false
        },
        'Netball': {
            periodName: 'Quarter',
            defaultPeriods: 4,
            defaultDuration: 15,
            defaultBreak: 3,
            hasExtraTime: true,
            extraTimePeriods: 2,
            extraTimeDuration: 7,
            hasTimeControl: false
        },
        'Handball': {
            periodName: 'Half',
            defaultPeriods: 2,
            defaultDuration: 30,
            defaultBreak: 10,
            hasExtraTime: true,
            extraTimePeriods: 2,
            extraTimeDuration: 5,
            hasTimeControl: false
        },
        'Chess': {
            periodName: 'Game',
            defaultPeriods: 1,
            defaultDuration: 0,
            defaultBreak: 0,
            hasExtraTime: false,
            hasTimeControl: true,
            timeControls: ['Classical (90+30)', 'Rapid (15+10)', 'Blitz (3+2)', 'Bullet (1+0)', 'Custom']
        },
        'Table Tennis': {
            periodName: 'Game',
            defaultPeriods: 7, // Best of 7
            defaultDuration: 0, // Points-based
            defaultBreak: 1,
            hasExtraTime: false,
            pointsToWin: 11,
            hasTimeControl: false
        },
        'Badminton': {
            periodName: 'Game',
            defaultPeriods: 3, // Best of 3
            defaultDuration: 0,
            defaultBreak: 2,
            hasExtraTime: false,
            pointsToWin: 21,
            hasTimeControl: false
        },
        'Tennis': {
            periodName: 'Set',
            defaultPeriods: 3, // Best of 3 or 5
            defaultDuration: 0,
            defaultBreak: 0,
            hasExtraTime: false,
            gamesPerSet: 6,
            tiebreak: true,
            hasTimeControl: false,
            isRacquetSport: true,
            defaultMatchFormat: 'best_of_3',
            matchFormats: ['best_of_1', 'best_of_3', 'best_of_5'],
            deuceRules: ['standard', 'golden_point', 'two_deuce_golden'],
            defaultDeuceRule: 'standard',
            defaultGamesPerSet: 6,
            gamesPerSetOptions: [4, 6],
            defaultTiebreakerPoints: 7,
            tiebreakerPointOptions: [7, 10, 12],
            singleSetTiebreakerOptions: ['standard', 'super_tiebreaker'],
            defaultSingleSetTiebreaker: 'super_tiebreaker'
        },
        'Swimming': {
            periodName: 'Heat',
            defaultPeriods: 1,
            defaultDuration: 0,
            defaultBreak: 0,
            hasExtraTime: false,
            hasTimeControl: false
        },
        'Athletics Track and Field': {
            periodName: 'Event',
            defaultPeriods: 1,
            defaultDuration: 0,
            defaultBreak: 0,
            hasExtraTime: false,
            hasTimeControl: false
        },
        'Karate': {
            periodName: 'Round',
            defaultPeriods: 1,
            defaultDuration: 3,
            defaultBreak: 0,
            hasExtraTime: true,
            extraTimePeriods: 1,
            extraTimeDuration: 2,
            hasTimeControl: false
        },
        'Taekwondo': {
            periodName: 'Round',
            defaultPeriods: 3,
            defaultDuration: 2,
            defaultBreak: 1,
            hasExtraTime: true,
            extraTimePeriods: 1,
            extraTimeDuration: 1,
            hasTimeControl: false
        },
        'Judo': {
            periodName: 'Match',
            defaultPeriods: 1,
            defaultDuration: 4,
            defaultBreak: 0,
            hasExtraTime: true,
            extraTimePeriods: 1,
            extraTimeDuration: 0, // Golden score (unlimited)
            hasTimeControl: false
        },
        'Padel': {
            periodName: 'Set',
            defaultPeriods: 3,
            defaultDuration: 0,
            defaultBreak: 0,
            hasExtraTime: false,
            gamesPerSet: 6,
            tiebreak: true,
            hasTimeControl: false,
            isRacquetSport: true,
            defaultMatchFormat: 'best_of_3',
            matchFormats: ['best_of_1', 'best_of_3', 'best_of_5'],
            deuceRules: ['standard', 'golden_point', 'two_deuce_golden'],
            defaultDeuceRule: 'golden_point', // Padel typically uses golden point
            defaultGamesPerSet: 6,
            gamesPerSetOptions: [4, 6],
            defaultTiebreakerPoints: 7,
            tiebreakerPointOptions: [7, 10, 12],
            singleSetTiebreakerOptions: ['standard', 'super_tiebreaker'],
            defaultSingleSetTiebreaker: 'super_tiebreaker'
        },
        'Squash': {
            periodName: 'Game',
            defaultPeriods: 5, // Best of 5 games
            defaultDuration: 0,
            defaultBreak: 1.5,
            hasExtraTime: false,
            pointsToWin: 11,
            hasTimeControl: false,
            isRacquetSport: true,
            defaultMatchFormat: 'best_of_5',
            matchFormats: ['best_of_3', 'best_of_5']
        },
        'E-Sports': {
            periodName: 'Game/Map',
            defaultPeriods: 3, // Best of 3 maps
            defaultDuration: 45, // Average game duration
            defaultBreak: 5,
            hasExtraTime: false,
            hasTimeControl: false,
            isDigitalSport: true,
            defaultMatchFormat: 'best_of_3',
            matchFormats: ['best_of_1', 'best_of_3', 'best_of_5', 'best_of_7']
        },
        'Motor Racing': {
            periodName: 'Race/Heat',
            defaultPeriods: 1,
            defaultDuration: 60, // Race duration in minutes
            defaultBreak: 30,
            hasExtraTime: false,
            hasTimeControl: false,
            hasQualifying: true,
            qualifyingFormat: ['Single Lap', 'Group Qualifying', 'Knockout'],
            raceFormats: ['Sprint Race', 'Feature Race', 'Endurance'],
            defaultLaps: 25
        }
    };

    // Get sport-specific settings or default
    const getSportSettings = (sport) => {
        return sportGameSettings[sport] || {
            periodName: 'Period',
            defaultPeriods: 2,
            defaultDuration: 30,
            defaultBreak: 10,
            hasExtraTime: false,
            hasTimeControl: false
        };
    };

    // Gender categories for tournaments
    const genderCategories = ['Boys', 'Girls', 'Mens', 'Womens'];
    
    // Age groups for tournaments
    const ageGroups = [
        'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 
        'U15', 'U16', 'U17', 'U18', 'U19', 
        'U21', 'U23', 'Senior', 'Open'
    ];
    
    // Age categories for athletes (legacy support)
    const ageCategories = [
        'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 
        'U15', 'U16', 'U17', 'U18', 'U19', 
        'U21', 'U23', 'Senior', 'Open'
    ];
    
    const tournamentStatuses = ['upcoming', 'registration_open', 'in_progress', 'completed', 'cancelled'];
    const userRoles = ['athlete', 'coach', 'scout', 'school_admin', 'organizer'];
    
    // Admin-specific roles with hierarchy
    const adminRoles = [
        { role: 'tournament_chairman', name: 'Tournament Chairman', description: 'Full access, can create roles & accounts' },
        { role: 'tournament_director', name: 'Tournament Director', description: 'All access except create tournament, no import/export' },
        { role: 'admin_team', name: 'Admin Team', description: 'All access except create tournament, can import/export' },
        { role: 'official_referee', name: 'Official / Referee', description: 'View tournaments, update scores & MVP for matches' },
        { role: 'account_manager', name: 'Account Manager', description: 'All access except create tournament, can view all accounts' }
    ];
    
    // Permission checks based on admin role — master_admin has all permissions
    const isMasterOrLegacyAdmin = () => {
        return ['master_admin', 'admin', 'tournament_chairman'].includes(user?.role);
    };

    const canCreateTournament = () => {
        return isMasterOrLegacyAdmin();
    };
    
    const canImportExport = () => {
        return isMasterOrLegacyAdmin() || ['admin_team', 'account_manager', 'ministry_admin'].includes(user?.role);
    };
    
    const canCreateAdminAccounts = () => {
        return isMasterOrLegacyAdmin();
    };
    
    const canManageRoles = () => {
        return isMasterOrLegacyAdmin();
    };
    
    const canViewAccounts = () => {
        return isMasterOrLegacyAdmin() || ['account_manager', 'tournament_director', 'admin_team', 'ministry_admin', 'education_admin', 'nock_admin'].includes(user?.role);
    };
    
    const canUpdateScores = () => {
        return isMasterOrLegacyAdmin() || ['official_referee', 'tournament_director', 'federation_admin'].includes(user?.role);
    };
    
    const isOfficialRefereeOnly = () => {
        return user?.role === 'official_referee';
    };
    
    // Accounting access
    const canAccessAccounting = () => {
        return isMasterOrLegacyAdmin() || ['account_manager', 'ministry_admin', 'education_admin'].includes(user?.role);
    };
    
    const paymentMethods = ['Cash', 'M-Pesa', 'Bank Transfer', 'Card Payment', 'Online Payment'];

    // Role-based tab visibility from RBAC backend
    const isTabVisible = (tabName) => {
        if (!rbacPermissions || !rbacPermissions.visible_tabs) {
            // Safe default: hide sensitive tabs (marketplace) until RBAC loads;
            // show others to avoid empty admin during initial mount.
            if (tabName === 'marketplace') return false;
            return true;
        }
        return rbacPermissions.visible_tabs.includes(tabName);
    };
    const currencies = [
        { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
        { code: 'USD', name: 'US Dollar', symbol: '$' },
        { code: 'EUR', name: 'Euro', symbol: '€' },
        { code: 'GBP', name: 'British Pound', symbol: '£' },
        { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
        { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
        { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
        { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
        { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
        { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw' },
        { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
        { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
        { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD' },
        { code: 'XOF', name: 'West African CFA', symbol: 'CFA' },
        { code: 'XAF', name: 'Central African CFA', symbol: 'FCFA' }
    ];

    // Form states
    const [tournamentForm, setTournamentForm] = useState({
        name: '', sport: '', disciplines: [], description: '', location: '', venue: '',
        start_date: '', end_date: '', registration_deadline: '',
        gender_categories: [], // Boys, Girls, Mens, Womens
        age_groups: [], // Under 9, Under 10, etc.
        max_participants: 100, 
        // Financial - Registration
        currency: 'KES',
        entry_fee: 0,
        // Discipline-based fees
        fee_1_discipline: 0,
        fee_2_disciplines: 0,
        fee_3_disciplines: 0,
        fee_4_disciplines: 0,
        fee_5_disciplines: 0,
        fee_per_discipline: false,  // Toggle for discipline-based pricing
        registration_includes: '',
        accepted_payment_methods: [],
        early_bird_discount: 0,
        early_bird_deadline: '',
        // Prize Money
        prize_pool: 0,
        prize_1st: 0,
        prize_2nd: 0,
        prize_3rd: 0,
        prize_per_category: false,
        tournament_status: 'upcoming',
        // Game Settings
        game_settings: {
            period_duration: 0,      // Duration per period/half in minutes
            num_periods: 2,          // Number of periods/halves
            break_duration: 0,       // Half-time/break duration in minutes
            extra_time_enabled: false,
            extra_time_duration: 0,  // Extra time duration per period in minutes
            extra_time_periods: 2,   // Number of extra time periods
            time_control: '',        // For chess (classical, rapid, blitz)
            custom_rules: '',        // Any custom rules text
            // Tennis/Padel specific settings
            match_format: 'best_of_3',        // best_of_1, best_of_3, best_of_5
            games_per_set: 6,                 // 4 or 6 games per set
            deuce_rule: 'standard',           // standard, golden_point, two_deuce_golden
            tiebreaker_points: 7,             // Points needed to win tiebreaker
            single_set_tiebreaker: 'super_tiebreaker', // For best_of_1 format
            use_default_rules: true           // Use default tournament rules
        }
    });

    const [athleteForm, setAthleteForm] = useState({
        full_name: '', date_of_birth: '', age_category: '', country: 'Kenya',
        county: '', subcounty: '', city: '', school: '', academy: '',
        sports: [], mobile_number: '', bio: '', verified: false,
        profile_location: null  // Manual location entry for admin-created athletes
    });

    const [schoolForm, setSchoolForm] = useState({
        school_name: '', country: 'Kenya', city: '', sports: [],
        sport_rankings: {},  // e.g., { "Basketball": { rank: 1, points: 500, wins: 10, losses: 2 } }
        rank: 1, points: 0, wins: 0, losses: 0, tournaments_participated: 0, athletes_count: 0,
        institution_type: 'sports_academy',
        fee_waiver_type: 'none', fee_waiver_amount: 0, fee_waiver_reason: ''
    });

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/admin/login');
            return;
        }
        
        // List of allowed admin panel roles
        const allowedRoles = [
            'master_admin', 'ministry_admin', 'education_admin', 'nock_admin', 'federation_admin',
            'club_admin', 'school_admin',
            'admin', 'tournament_chairman', 'tournament_director', 
            'official_referee', 'account_manager', 'admin_team'
        ];
        
        if (!allowedRoles.includes(user?.role)) {
            toast.error('Admin access required');
            navigate('/admin/login');
            return;
        }
        
        // Set default tab for officials to "matches"
        if (user?.role === 'official_referee') {
            setActiveTab('matches');
        }
        
        loadData();
    }, [isAuthenticated, user, navigate]);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [statsRes, athletesRes, tournamentsRes, usersRes, schoolsRes, settingsRes, rbacRes] = await Promise.all([
                api.get('/admin/stats'),
                api.get('/admin/athletes'),
                api.get('/admin/tournaments'),
                api.get('/admin/users'),
                api.get('/school-rankings'),
                api.get('/admin/settings').catch(() => ({ data: null })),
                api.get('/rbac/my-permissions').catch(() => ({ data: null }))
            ]);
            setStats(statsRes.data);
            setAthletes(athletesRes.data.athletes);
            setTournaments(tournamentsRes.data.tournaments);
            setUsers(usersRes.data.users);
            setSchoolRankings(schoolsRes.data);
            setSettings(settingsRes.data);
            if (rbacRes.data) setRbacPermissions(rbacRes.data);
        } catch (error) {
            console.error('Failed to load admin data:', error);
            if (error.response?.status === 403) {
                toast.error('Admin access required');
                navigate('/admin/login');
            }
        } finally {
            setLoading(false);
        }
    };

    // Tournament handlers
    const handleSaveTournament = async () => {
        try {
            if (editingTournament) {
                await api.put(`/admin/tournaments/${editingTournament.id}`, tournamentForm);
                toast.success('Tournament updated successfully');
            } else {
                await api.post('/admin/tournaments', tournamentForm);
                toast.success('Tournament created successfully');
            }
            setShowTournamentDialog(false);
            resetTournamentForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save tournament');
        }
    };

    const handleEditTournament = (tournament) => {
        setEditingTournament(tournament);
        setTournamentForm({
            name: tournament.name || '',
            sport: tournament.sport || '',
            disciplines: tournament.disciplines || [],
            description: tournament.description || '',
            location: tournament.location || '',
            venue: tournament.venue || '',
            start_date: tournament.start_date || '',
            end_date: tournament.end_date || '',
            registration_deadline: tournament.registration_deadline || '',
            gender_categories: tournament.gender_categories || [],
            age_groups: tournament.age_groups || [],
            max_participants: tournament.max_participants || 100,
            currency: tournament.currency || 'KES',
            entry_fee: tournament.entry_fee || 0,
            fee_1_discipline: tournament.fee_1_discipline || 0,
            fee_2_disciplines: tournament.fee_2_disciplines || 0,
            fee_3_disciplines: tournament.fee_3_disciplines || 0,
            fee_4_disciplines: tournament.fee_4_disciplines || 0,
            fee_5_disciplines: tournament.fee_5_disciplines || 0,
            fee_per_discipline: tournament.fee_per_discipline || false,
            registration_includes: tournament.registration_includes || '',
            accepted_payment_methods: tournament.accepted_payment_methods || [],
            early_bird_discount: tournament.early_bird_discount || 0,
            early_bird_deadline: tournament.early_bird_deadline || '',
            prize_pool: tournament.prize_pool || 0,
            prize_1st: tournament.prize_1st || 0,
            prize_2nd: tournament.prize_2nd || 0,
            prize_3rd: tournament.prize_3rd || 0,
            prize_per_category: tournament.prize_per_category || false,
            tournament_status: tournament.tournament_status || 'upcoming',
            game_settings: tournament.game_settings || {
                period_duration: 0,
                num_periods: 2,
                break_duration: 0,
                extra_time_enabled: false,
                extra_time_duration: 0,
                extra_time_periods: 2,
                time_control: '',
                custom_rules: ''
            }
        });
        setShowTournamentDialog(true);
    };

    const resetTournamentForm = () => {
        setEditingTournament(null);
        setTournamentForm({
            name: '', sport: '', disciplines: [], description: '', location: '', venue: '',
            start_date: '', end_date: '', registration_deadline: '',
            gender_categories: [], age_groups: [], max_participants: 100, currency: 'KES',
            entry_fee: 0, 
            fee_1_discipline: 0, fee_2_disciplines: 0, fee_3_disciplines: 0, 
            fee_4_disciplines: 0, fee_5_disciplines: 0, fee_per_discipline: false,
            registration_includes: '', accepted_payment_methods: [],
            early_bird_discount: 0, early_bird_deadline: '',
            prize_pool: 0, prize_1st: 0, prize_2nd: 0, prize_3rd: 0, prize_per_category: false,
            tournament_status: 'upcoming',
            game_settings: {
                period_duration: 0,
                num_periods: 2,
                break_duration: 0,
                extra_time_enabled: false,
                extra_time_duration: 0,
                extra_time_periods: 2,
                time_control: '',
                custom_rules: ''
            }
        });
    };

    const handleDeleteTournament = async (tournamentId) => {
        if (!window.confirm('Are you sure you want to delete this tournament?')) return;
        try {
            await api.delete(`/admin/tournaments/${tournamentId}`);
            toast.success('Tournament deleted successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to delete tournament');
        }
    };

    // Athlete handlers
    const handleSaveAthlete = async () => {
        try {
            if (editingAthlete) {
                await api.put(`/admin/athletes/${editingAthlete.id}`, athleteForm);
                toast.success('Athlete updated successfully');
            } else {
                await api.post('/admin/athletes', athleteForm);
                toast.success('Athlete created successfully');
            }
            setShowAthleteDialog(false);
            resetAthleteForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save athlete');
        }
    };

    const handleEditAthlete = (athlete) => {
        setEditingAthlete(athlete);
        setAthleteForm({
            full_name: athlete.full_name || '',
            date_of_birth: athlete.date_of_birth || '',
            age_category: athlete.age_category || '',
            country: athlete.country || 'Kenya',
            county: athlete.county || '',
            subcounty: athlete.subcounty || '',
            city: athlete.city || '',
            school: athlete.school || '',
            academy: athlete.academy || '',
            sports: athlete.sports || [],
            mobile_number: athlete.mobile_number || '',
            bio: athlete.bio || '',
            verified: athlete.verified || false
        });
        setShowAthleteDialog(true);
    };

    const resetAthleteForm = () => {
        setEditingAthlete(null);
        setAthleteForm({
            full_name: '', date_of_birth: '', age_category: '', country: 'Kenya',
            county: '', subcounty: '', city: '', school: '', academy: '',
            sports: [], mobile_number: '', bio: '', verified: false,
            profile_location: null
        });
    };

    const handleVerifyAthlete = async (athleteId) => {
        try {
            await api.put(`/admin/athletes/${athleteId}/verify`);
            toast.success('Athlete verified successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to verify athlete');
        }
    };

    const handleRejectAthlete = async (athleteId) => {
        try {
            await api.put(`/admin/athletes/${athleteId}/reject`);
            toast.success('Athlete verification rejected');
            loadData();
        } catch (error) {
            toast.error('Failed to reject athlete');
        }
    };

    const handleDeleteAthlete = async (athleteId) => {
        if (!window.confirm('Are you sure you want to delete this athlete?')) return;
        try {
            await api.delete(`/admin/athletes/${athleteId}`);
            toast.success('Athlete deleted successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to delete athlete');
        }
    };

    // School handlers
    const handleSaveSchool = async () => {
        try {
            // Prepare data with sports array and legacy sport field
            const formData = {
                ...schoolForm,
                sport: schoolForm.sports[0] || 'Football',  // For backwards compatibility
                sports: schoolForm.sports,
                sport_rankings: schoolForm.sport_rankings
            };
            
            if (editingSchool) {
                await api.put(`/admin/school-rankings/${editingSchool.id}`, formData);
                toast.success('School updated successfully');
            } else {
                await api.post('/admin/school-rankings', formData);
                toast.success('School created successfully');
            }
            setShowSchoolDialog(false);
            resetSchoolForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save school');
        }
    };

    // Helper function to update sport ranking
    const updateSportRanking = (sport, field, value) => {
        setSchoolForm(prev => ({
            ...prev,
            sport_rankings: {
                ...prev.sport_rankings,
                [sport]: {
                    ...(prev.sport_rankings[sport] || { rank: 999, points: 0, wins: 0, losses: 0, tournaments_participated: 0 }),
                    [field]: value
                }
            }
        }));
    };

    // Initialize sport rankings when sports are selected
    const handleSportToggle = (sport, isSelected) => {
        if (isSelected) {
            // Add sport to array
            setSchoolForm(prev => ({
                ...prev,
                sports: [...prev.sports, sport],
                sport_rankings: {
                    ...prev.sport_rankings,
                    [sport]: prev.sport_rankings[sport] || { rank: 999, points: 0, wins: 0, losses: 0, tournaments_participated: 0 }
                }
            }));
        } else {
            // Remove sport from array and rankings
            setSchoolForm(prev => {
                const newRankings = { ...prev.sport_rankings };
                delete newRankings[sport];
                return {
                    ...prev,
                    sports: prev.sports.filter(s => s !== sport),
                    sport_rankings: newRankings
                };
            });
        }
    };

    const handleEditSchool = (school) => {
        setEditingSchool(school);
        // Handle both old 'sport' (string) and new 'sports' (array) field
        let sportsArray = [];
        if (school.sports && Array.isArray(school.sports)) {
            sportsArray = school.sports;
        } else if (school.sport) {
            sportsArray = [school.sport];
        }
        
        setSchoolForm({
            school_name: school.school_name || '',
            country: school.country || 'Kenya',
            city: school.city || '',
            sports: sportsArray,
            sport_rankings: school.sport_rankings || {},
            rank: school.rank || 1,
            points: school.points || 0,
            wins: school.wins || 0,
            losses: school.losses || 0,
            tournaments_participated: school.tournaments_participated || 0,
            athletes_count: school.athletes_count || 0,
            institution_type: school.institution_type || 'sports_academy',
            fee_waiver_type: school.fee_waiver_type || 'none',
            fee_waiver_amount: school.fee_waiver_amount || 0,
            fee_waiver_reason: school.fee_waiver_reason || ''
        });
        setShowSchoolDialog(true);
    };

    const resetSchoolForm = () => {
        setEditingSchool(null);
        setSchoolForm({
            school_name: '', country: 'Kenya', city: '', sports: [],
            sport_rankings: {},
            rank: 1, points: 0, wins: 0, losses: 0, tournaments_participated: 0, athletes_count: 0,
            institution_type: 'sports_academy',
            fee_waiver_type: 'none', fee_waiver_amount: 0, fee_waiver_reason: ''
        });
    };

    const handleDeleteSchool = async (schoolId) => {
        if (!window.confirm('Are you sure you want to delete this school?')) return;
        try {
            await api.delete(`/admin/school-rankings/${schoolId}`);
            toast.success('School deleted successfully');
            loadData();
        } catch (error) {
            toast.error('Failed to delete school');
        }
    };

    // User role handlers
    const handleOpenRoleDialog = (userItem) => {
        setEditingUserRole(userItem);
        setShowRoleDialog(true);
    };

    const handleUpdateUserRole = async (newRole) => {
        try {
            await api.put(`/admin/users/${editingUserRole.id}/role`, { role: newRole });
            toast.success(`User role updated to ${newRole}`);
            setShowRoleDialog(false);
            setEditingUserRole(null);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update role');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await api.delete(`/admin/users/${userId}`);
            toast.success('User deleted successfully');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    };

    // Login as another user (impersonation)
    const handleLoginAsUser = async (targetUser) => {
        // Only Chairman and Admin can impersonate
        if (!['tournament_chairman', 'admin', 'master_admin'].includes(user?.role)) {
            toast.error('Only Chairman and Admin can login as other users');
            return;
        }

        if (!window.confirm(`Are you sure you want to login as ${targetUser.full_name}? You will be logged out of your admin account.`)) {
            return;
        }

        try {
            const response = await api.post(`/admin/impersonate/${targetUser.id}`);
            const { access_token, user: impersonatedUser } = response.data;
            
            // Store admin's original token for potential "return to admin" feature
            localStorage.setItem('admin_original_token', token);
            localStorage.setItem('admin_original_user', JSON.stringify(user));
            
            // Set the new token and redirect
            localStorage.setItem('token', access_token);
            
            toast.success(`Now logged in as ${impersonatedUser.full_name}`);
            
            // Reload the page to apply new auth context
            window.location.href = '/dashboard';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to login as user');
        }
    };

    // Bulk user selection handlers
    const handleSelectAllUsers = (checked) => {
        if (checked) {
            // Select all non-admin users from filtered list
            const selectableUsers = filteredUsers.filter(u => u.role !== 'admin').map(u => u.id);
            setSelectedUsers(selectableUsers);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId, checked) => {
        if (checked) {
            setSelectedUsers([...selectedUsers, userId]);
        } else {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
        }
    };

    const handleBulkRoleChange = async (newRole) => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }
        
        try {
            let successCount = 0;
            let errorCount = 0;
            
            for (const userId of selectedUsers) {
                try {
                    await api.put(`/admin/users/${userId}/role`, { role: newRole });
                    successCount++;
                } catch (error) {
                    errorCount++;
                }
            }
            
            if (successCount > 0) {
                toast.success(`Updated ${successCount} user(s) to ${newRole}`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to update ${errorCount} user(s)`);
            }
            
            setSelectedUsers([]);
            setShowBulkRoleDialog(false);
            loadData();
        } catch (error) {
            toast.error('Failed to update users');
        }
    };

    const handleBulkDeleteUsers = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }
        
        if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)?`)) return;
        
        try {
            let successCount = 0;
            let errorCount = 0;
            
            for (const userId of selectedUsers) {
                try {
                    await api.delete(`/admin/users/${userId}`);
                    successCount++;
                } catch (error) {
                    errorCount++;
                }
            }
            
            if (successCount > 0) {
                toast.success(`Deleted ${successCount} user(s)`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to delete ${errorCount} user(s)`);
            }
            
            setSelectedUsers([]);
            loadData();
        } catch (error) {
            toast.error('Failed to delete users');
        }
    };

    // Bulk Athletes handlers
    const handleSelectAllAthletes = (checked) => {
        if (checked) {
            setSelectedAthletes(filteredAthletes.map(a => a.id));
        } else {
            setSelectedAthletes([]);
        }
    };

    const handleSelectAthlete = (athleteId, checked) => {
        if (checked) {
            setSelectedAthletes([...selectedAthletes, athleteId]);
        } else {
            setSelectedAthletes(selectedAthletes.filter(id => id !== athleteId));
        }
    };

    const handleBulkVerifyAthletes = async () => {
        if (selectedAthletes.length === 0) return;
        
        try {
            let successCount = 0;
            for (const athleteId of selectedAthletes) {
                try {
                    await api.put(`/admin/athletes/${athleteId}/verify`);
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Verified ${successCount} athlete(s)`);
            setSelectedAthletes([]);
            loadData();
        } catch (error) {
            toast.error('Failed to verify athletes');
        }
    };

    const handleBulkRejectAthletes = async () => {
        if (selectedAthletes.length === 0) return;
        
        try {
            let successCount = 0;
            for (const athleteId of selectedAthletes) {
                try {
                    await api.put(`/admin/athletes/${athleteId}/reject`);
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Rejected ${successCount} athlete(s)`);
            setSelectedAthletes([]);
            loadData();
        } catch (error) {
            toast.error('Failed to reject athletes');
        }
    };

    const handleBulkDeleteAthletes = async () => {
        if (selectedAthletes.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedAthletes.length} athlete(s)?`)) return;
        
        try {
            let successCount = 0;
            for (const athleteId of selectedAthletes) {
                try {
                    await api.delete(`/admin/athletes/${athleteId}`);
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Deleted ${successCount} athlete(s)`);
            setSelectedAthletes([]);
            loadData();
        } catch (error) {
            toast.error('Failed to delete athletes');
        }
    };

    // Bulk Tournaments handlers
    const handleSelectAllTournaments = (checked) => {
        if (checked) {
            setSelectedTournaments(filteredTournaments.map(t => t.id));
        } else {
            setSelectedTournaments([]);
        }
    };

    const handleSelectTournament = (tournamentId, checked) => {
        if (checked) {
            setSelectedTournaments([...selectedTournaments, tournamentId]);
        } else {
            setSelectedTournaments(selectedTournaments.filter(id => id !== tournamentId));
        }
    };

    const handleBulkDeleteTournaments = async () => {
        if (selectedTournaments.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedTournaments.length} tournament(s)?`)) return;
        
        try {
            let successCount = 0;
            for (const tournamentId of selectedTournaments) {
                try {
                    await api.delete(`/admin/tournaments/${tournamentId}`);
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Deleted ${successCount} tournament(s)`);
            setSelectedTournaments([]);
            loadData();
        } catch (error) {
            toast.error('Failed to delete tournaments');
        }
    };

    const handleBulkUpdateTournamentStatus = async (status) => {
        if (selectedTournaments.length === 0) return;
        
        try {
            let successCount = 0;
            for (const tournamentId of selectedTournaments) {
                try {
                    await api.put(`/admin/tournaments/${tournamentId}`, { tournament_status: status });
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Updated ${successCount} tournament(s) to ${status.replace(/_/g, ' ')}`);
            setSelectedTournaments([]);
            loadData();
        } catch (error) {
            toast.error('Failed to update tournaments');
        }
    };

    // Bulk Schools handlers
    const handleSelectAllSchools = (checked) => {
        if (checked) {
            setSelectedSchools(schoolRankings.map(s => s.id));
        } else {
            setSelectedSchools([]);
        }
    };

    const handleSelectSchool = (schoolId, checked) => {
        if (checked) {
            setSelectedSchools([...selectedSchools, schoolId]);
        } else {
            setSelectedSchools(selectedSchools.filter(id => id !== schoolId));
        }
    };

    const handleBulkDeleteSchools = async () => {
        if (selectedSchools.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedSchools.length} school(s)?`)) return;
        
        try {
            let successCount = 0;
            for (const schoolId of selectedSchools) {
                try {
                    await api.delete(`/admin/school-rankings/${schoolId}`);
                    successCount++;
                } catch (error) {}
            }
            toast.success(`Deleted ${successCount} school(s)`);
            setSelectedSchools([]);
            loadData();
        } catch (error) {
            toast.error('Failed to delete schools');
        }
    };

    // Import/Export handlers
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const fileName = file.name.toLowerCase();
        
        // For Excel files (.xlsx, .xls) or CSV files, use the new file upload endpoint
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.csv')) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                
                const endpoint = importType === 'athletes' 
                    ? '/admin/import/athletes/upload' 
                    : '/admin/import/schools/upload';
                
                const response = await api.post(endpoint, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                toast.success(`Imported ${response.data.imported} records. ${response.data.errors?.length || 0} errors.`);
                if (response.data.errors?.length > 0) {
                    console.log('Import errors:', response.data.errors);
                }
                setShowImportDialog(false);
                loadData();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to import data');
            }
            return;
        }
        
        // For JSON files, parse and send as JSON (backward compatibility)
        if (fileName.endsWith('.json')) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = JSON.parse(e.target.result);
                    const endpoint = importType === 'athletes' ? '/admin/import/athletes' : '/admin/import/schools';
                    const response = await api.post(endpoint, data);
                    toast.success(`Imported ${response.data.imported} records. ${response.data.errors?.length || 0} errors.`);
                    setShowImportDialog(false);
                    loadData();
                } catch (error) {
                    toast.error('Failed to import data');
                }
            };
            reader.readAsText(file);
            return;
        }
        
        toast.error('Please upload a CSV, Excel (.xlsx, .xls), or JSON file');
    };

    const parseCSV = (csvText) => {
        const lines = csvText.trim().split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
        const data = [];
        
        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const obj = {};
            headers.forEach((header, idx) => {
                let value = values[idx] || '';
                // Handle sports as array
                if (header === 'sports' && value) {
                    obj[header] = value.split(';').map(s => s.trim());
                } else if (['rank', 'points', 'wins', 'losses', 'tournaments_participated', 'athletes_count', 'max_participants', 'entry_fee', 'prize_pool'].includes(header)) {
                    obj[header] = parseFloat(value) || 0;
                } else if (header === 'verified') {
                    obj[header] = value.toLowerCase() === 'true';
                } else {
                    obj[header] = value;
                }
            });
            data.push(obj);
        }
        return data;
    };

    const handleExport = async (type) => {
        try {
            const response = await api.get(`/admin/export/${type}`);
            const data = response.data.data;
            
            // Convert to CSV
            if (data.length === 0) {
                toast.error('No data to export');
                return;
            }
            
            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(h => {
                    let val = row[h];
                    if (Array.isArray(val)) val = val.join(';');
                    if (typeof val === 'object') val = JSON.stringify(val);
                    if (typeof val === 'string' && val.includes(',')) val = `"${val}"`;
                    return val ?? '';
                }).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${data.length} ${type}`);
        } catch (error) {
            toast.error(`Failed to export ${type}`);
        }
    };

    // Settings handlers
    const handleSaveSettings = async () => {
        try {
            await api.put('/admin/settings', settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error('Failed to save settings');
        }
    };

    // Admin account creation handler
    const handleCreateAdminAccount = async () => {
        if (!newAdminForm.full_name || !newAdminForm.email || !newAdminForm.password) {
            toast.error('Please fill all required fields');
            return;
        }
        
        try {
            await api.post('/admin/create-admin', {
                full_name: newAdminForm.full_name,
                email: newAdminForm.email,
                password: newAdminForm.password,
                role: newAdminForm.admin_role
            });
            toast.success(`Admin account created for ${newAdminForm.full_name}`);
            setShowAdminDialog(false);
            setNewAdminForm({ full_name: '', email: '', password: '', admin_role: 'admin_team' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create admin account');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const filteredAthletes = athletes.filter(a => {
        if (athleteFilter.verified !== 'all') {
            if (athleteFilter.verified === 'true' && !a.verified) return false;
            if (athleteFilter.verified === 'false' && a.verified) return false;
        }
        if (athleteFilter.sport !== 'all' && !a.sports?.includes(athleteFilter.sport)) return false;
        if (athleteFilter.school !== 'all' && a.school !== athleteFilter.school) return false;
        if (athleteFilter.search) {
            const q = athleteFilter.search.toLowerCase();
            if (!a.full_name?.toLowerCase().includes(q) && !a.country?.toLowerCase().includes(q)) return false;
        }
        return true;
    });

    // Get unique schools from athletes for filter dropdown
    const uniqueSchools = [...new Set(athletes.map(a => a.school).filter(Boolean))].sort();

    const filteredTournaments = tournaments.filter(t => {
        if (tournamentFilter.status !== 'all' && t.tournament_status !== tournamentFilter.status) return false;
        if (tournamentFilter.sport !== 'all' && t.sport !== tournamentFilter.sport) return false;
        if (tournamentFilter.search) {
            const q = tournamentFilter.search.toLowerCase();
            if (!t.name?.toLowerCase().includes(q) && !t.location?.toLowerCase().includes(q)) return false;
        }
        return true;
    });

    const filteredUsers = users.filter(u => {
        if (userFilter.role !== 'all' && u.role !== userFilter.role) return false;
        if (userFilter.search) {
            const q = userFilter.search.toLowerCase();
            if (!u.full_name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
        }
        return true;
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="fixed inset-0 grid-lines pointer-events-none" style={{ zIndex: -1 }} />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1600px] mx-auto px-4 md:px-6">
                    <div className="flex items-center justify-between h-14 md:h-16">
                        <div className="flex items-center gap-2 md:gap-4">
                            <Link to="/" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-sm md:text-lg tracking-tight uppercase hidden sm:inline">
                                    NextGen<span className="text-primary"> Tournaments</span>
                                </span>
                            </Link>
                            <span className="px-2 py-0.5 md:px-3 md:py-1 bg-primary/20 text-primary text-[10px] md:text-xs font-bold uppercase tracking-wider">
                                Admin
                            </span>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                            <span className="text-xs md:text-sm text-muted-foreground hidden md:inline">{user?.email}</span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleLogout}
                                className="border-white/10 text-xs md:text-sm px-2 md:px-4"
                                data-testid="admin-logout-btn"
                            >
                                <LogOut className="w-4 h-4 md:mr-2" />
                                <span className="hidden md:inline">Logout</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 md:py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
                    <div>
                        <h1 className="font-heading font-bold text-xl md:text-3xl uppercase tracking-tight mb-1 md:mb-2">
                            {rbacPermissions?.display_name || 'Admin'} Dashboard
                        </h1>
                        <p className="text-muted-foreground text-sm md:text-base">
                            {rbacPermissions?.role === 'master_admin' && 'Full platform control — All sports, federations, and system settings'}
                            {rbacPermissions?.role === 'ministry_admin' && 'Ministry of Sports — All sports oversight, national analytics, and accounts'}
                            {rbacPermissions?.role === 'education_admin' && 'Ministry of Education — Schools oversight, all sports, student athletes'}
                            {rbacPermissions?.role === 'nock_admin' && 'National Olympic Committee — Olympic sports, federations, and performance'}
                            {rbacPermissions?.role === 'federation_admin' && `Federation Admin — ${rbacPermissions?.accessible_sports?.[0] || 'Sport'} management`}
                            {rbacPermissions?.role === 'club_admin' && 'Club Admin — Athlete registration and tournament entry'}
                            {rbacPermissions?.role === 'school_admin' && 'School Admin — Student athletes and school tournaments'}
                            {!rbacPermissions && 'Manage athletes, tournaments, users, and platform settings'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {rbacPermissions && (
                            <div className="text-xs px-3 py-1.5 rounded border bg-cyan-500/10 border-cyan-500/30 text-cyan-400 font-medium" data-testid="role-badge">
                                Level {rbacPermissions.level} — {rbacPermissions.display_name}
                            </div>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadData()}
                            className="border-white/10"
                        >
                            <RefreshCw className="w-4 h-4 md:mr-2" />
                            <span className="hidden md:inline">Refresh</span>
                        </Button>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 md:space-y-8">
                    <TabsList className="bg-[#0d2847] border border-white/5 p-1 flex flex-wrap gap-1 h-auto">
                        {isTabVisible('overview') && (
                            <TabsTrigger value="overview" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-overview">
                                <BarChart3 className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Overview</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('athletes') && (
                            <TabsTrigger value="athletes" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-athletes">
                                <Users className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Athletes</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('tournaments') && (
                            <TabsTrigger value="tournaments" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-tournaments">
                                <Calendar className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Tournaments</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('users') && (
                            <TabsTrigger value="users" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-users">
                                <User className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Users</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('schools') && (
                            <TabsTrigger value="schools" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-schools">
                                <School className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Schools</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('registrations') && (
                            <TabsTrigger value="registrations" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-registrations">
                                <UserCheck className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Registrations</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('matches') && (
                            <TabsTrigger value="matches" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-matches">
                                <Activity className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Matches</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('brackets') && (
                            <TabsTrigger value="brackets" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-brackets">
                                <GitBranch className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Brackets</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('accounting') && (
                            <TabsTrigger value="accounting" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-accounting">
                                <DollarSign className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Accounting</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('notifications') && (
                            <TabsTrigger value="notifications" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-notifications">
                                <Bell className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Notifications</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('video_analysis') && (
                            <TabsTrigger value="video-analysis" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-video-analysis">
                                <Video className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Video Analysis</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('live_scoring') && (
                            <TabsTrigger value="live-scoring" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-live-scoring">
                                <Radio className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Live Scoring</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('streaming') && (
                            <TabsTrigger value="streaming" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-streaming">
                                <Radio className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Live Streaming</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('marketplace') && (
                            <TabsTrigger value="marketplace" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-marketplace">
                                <ShoppingBag className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Marketplace</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('rankings') && (
                            <TabsTrigger value="rankings" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-rankings">
                                <Trophy className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Rankings</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('rankings') && (
                            <TabsTrigger value="combined-rankings" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-combined-rankings">
                                <Sparkles className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Combined Rankings</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('federation') && (
                            <TabsTrigger value="federation" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-federation">
                                <Building2 className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Federation Intel</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('sponsorship') && (
                            <TabsTrigger value="sponsorship" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-sponsorship">
                                <Award className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Sponsorship</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('import_export') && (
                            <TabsTrigger value="import-export" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-import">
                                <FileSpreadsheet className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Import/Export</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('hierarchy') && (
                            <TabsTrigger value="hierarchy" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-hierarchy">
                                <Crown className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Hierarchy</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('talent_ai') && (
                            <TabsTrigger value="talent-ai" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-talent-ai">
                                <Brain className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Talent AI</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('applications') && (
                            <TabsTrigger value="applications" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-applications">
                                <UserPlus className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Applications</span>
                            </TabsTrigger>
                        )}
                        {isTabVisible('settings') && (
                            <TabsTrigger value="settings" className="data-[state=active]:bg-primary text-xs md:text-sm px-2 md:px-4 py-1.5 md:py-2" data-testid="admin-tab-settings">
                                <Settings className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                                <span className="hidden md:inline">Settings</span>
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6">
                        <OverviewTab 
                            stats={stats}
                            users={users}
                            setActiveTab={setActiveTab}
                            userFilter={userFilter}
                            setUserFilter={setUserFilter}
                        />
                    </TabsContent>

                    {/* Athletes Tab */}
                    <TabsContent value="athletes" className="space-y-6">
                        <AthletesTab
                            athletes={athletes}
                            filteredAthletes={filteredAthletes}
                            filter={athleteFilter}
                            setFilter={setAthleteFilter}
                            sports={sports}
                            uniqueSchools={uniqueSchools}
                            selectedAthletes={selectedAthletes}
                            onSelectAll={handleSelectAllAthletes}
                            onSelectAthlete={handleSelectAthlete}
                            onVerify={handleVerifyAthlete}
                            onReject={handleRejectAthlete}
                            onDelete={handleDeleteAthlete}
                            onEdit={handleEditAthlete}
                            onCreate={() => { resetAthleteForm(); setShowAthleteDialog(true); }}
                            onBulkVerify={handleBulkVerifyAthletes}
                            onBulkReject={handleBulkRejectAthletes}
                            onBulkDelete={handleBulkDeleteAthletes}
                            onClearSelection={() => setSelectedAthletes([])}
                        />
                    </TabsContent>

                    {/* Tournaments Tab */}
                    <TabsContent value="tournaments" className="space-y-6">
                        <TournamentsTab
                            tournaments={tournaments}
                            filteredTournaments={filteredTournaments}
                            filter={tournamentFilter}
                            setFilter={setTournamentFilter}
                            sports={sports}
                            tournamentStatuses={tournamentStatuses}
                            selectedTournaments={selectedTournaments}
                            onSelectAll={handleSelectAllTournaments}
                            onSelectTournament={handleSelectTournament}
                            onEdit={handleEditTournament}
                            onDelete={handleDeleteTournament}
                            onCreate={() => { resetTournamentForm(); setShowTournamentDialog(true); }}
                            onBulkStatusChange={handleBulkUpdateTournamentStatus}
                            onBulkDelete={handleBulkDeleteTournaments}
                            onClearSelection={() => setSelectedTournaments([])}
                            canCreateTournament={canCreateTournament()}
                        />
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users" className="space-y-6">
                        <UsersTab
                            users={users}
                            filteredUsers={filteredUsers}
                            filter={userFilter}
                            setFilter={setUserFilter}
                            userRoles={userRoles}
                            selectedUsers={selectedUsers}
                            currentUser={user}
                            onSelectAll={handleSelectAllUsers}
                            onSelectUser={handleSelectUser}
                            onRoleChange={handleOpenRoleDialog}
                            onDelete={handleDeleteUser}
                            onLoginAs={handleLoginAsUser}
                            onBulkRoleChange={() => setShowBulkRoleDialog(true)}
                            onBulkDelete={handleBulkDeleteUsers}
                            onClearSelection={() => setSelectedUsers([])}
                        />
                    </TabsContent>

                    {/* Schools Tab */}
                    <TabsContent value="schools" className="space-y-6">
                        <SchoolsTab
                            schoolRankings={schoolRankings}
                            selectedSchools={selectedSchools}
                            onSelectAll={handleSelectAllSchools}
                            onSelectSchool={handleSelectSchool}
                            onEdit={handleEditSchool}
                            onDelete={handleDeleteSchool}
                            onCreate={() => { resetSchoolForm(); setShowSchoolDialog(true); }}
                            onBulkDelete={handleBulkDeleteSchools}
                            onClearSelection={() => setSelectedSchools([])}
                            sports={sports}
                        />
                    </TabsContent>

                    {/* Registrations Tab */}
                    <TabsContent value="registrations" className="space-y-6">
                        <RegistrationsTab tournaments={tournaments} />
                    </TabsContent>

                    {/* Matches Tab */}
                    <TabsContent value="matches" className="space-y-6">
                        <MatchesTab tournaments={tournaments} token={token} user={user} />
                    </TabsContent>

                    {/* Brackets Tab */}
                    <TabsContent value="brackets" className="space-y-6">
                        <BracketTab tournaments={tournaments} token={token} />
                    </TabsContent>

                    {/* Accounting Tab */}
                    <TabsContent value="accounting" className="space-y-6">
                        <AccountingTab tournaments={tournaments} token={token} />
                    </TabsContent>

                    {/* Notifications Tab */}
                    <TabsContent value="notifications" className="space-y-6">
                        <NotificationsTab tournaments={tournaments} schools={schoolRankings} />
                    </TabsContent>

                    {/* Video Analysis Tab */}
                    <TabsContent value="video-analysis" className="space-y-6">
                        <VideoAnalysisTab sports={sports} />
                    </TabsContent>

                    {/* Live Scoring Tab */}
                    <TabsContent value="live-scoring" className="space-y-6">
                        <LiveScoringTab tournaments={tournaments} token={token} />
                    </TabsContent>

                    {/* Live Streaming Tab — tournament-attached stream control */}
                    {isTabVisible('streaming') && (
                        <TabsContent value="streaming" className="space-y-6">
                            <LiveStreamingTab token={token} />
                        </TabsContent>
                    )}

                    {/* Marketplace Tab — MASTER ADMIN ONLY (completely hidden for others) */}
                    {isTabVisible('marketplace') && (
                        <TabsContent value="marketplace" className="space-y-6">
                            <MarketplaceTab token={token} />
                        </TabsContent>
                    )}

                    {/* Rankings Tab */}
                    <TabsContent value="rankings" className="space-y-6">
                        <RankingsTab token={token} athletes={athletes} tournaments={tournaments} />
                    </TabsContent>

                    {/* Combined Rankings Tab */}
                    <TabsContent value="combined-rankings" className="space-y-6">
                        <CombinedRankingsTab sports={sports} />
                    </TabsContent>

                    {/* Federation Intelligence Tab */}
                    {isTabVisible('federation') && (
                        <TabsContent value="federation" className="space-y-6">
                            <FederationDashboardTab sports={sports} />
                        </TabsContent>
                    )}

                    {/* Sponsorship Tab */}
                    <TabsContent value="sponsorship" className="space-y-6">
                        <SponsorshipTab token={token} />
                    </TabsContent>

                    {/* Import/Export Tab */}
                    <TabsContent value="import-export" className="space-y-6">
                        <ImportExportTab
                            stats={stats}
                            onImportAthletes={() => { setImportType('athletes'); setShowImportDialog(true); }}
                            onImportSchools={() => { setImportType('schools'); setShowImportDialog(true); }}
                            onExport={handleExport}
                        />
                    </TabsContent>

                    {/* Settings Tab */}
                    <TabsContent value="hierarchy" className="space-y-6">
                        <HierarchyTab token={token} />
                    </TabsContent>

                    {isTabVisible('talent_ai') && (
                        <TabsContent value="talent-ai" className="space-y-6">
                            <TalentAITab token={token} />
                        </TabsContent>
                    )}

                    {isTabVisible('applications') && (
                        <TabsContent value="applications" className="space-y-6">
                            <ApplicationsTab token={token} userRole={user?.role} />
                        </TabsContent>
                    )}

                    <TabsContent value="settings" className="space-y-6">
                        <SettingsTab
                            user={user}
                            users={users}
                            settings={settings}
                            setSettings={setSettings}
                            adminRoles={adminRoles}
                            canCreateAdminAccounts={canCreateAdminAccounts()}
                            onCreateAdmin={() => setShowAdminDialog(true)}
                            onSaveSettings={handleSaveSettings}
                        />
                    </TabsContent>
                </Tabs>
            </main>

            {/* Tournament Dialog */}
            <Dialog open={showTournamentDialog} onOpenChange={setShowTournamentDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            {editingTournament ? 'Edit Tournament' : 'Create Tournament'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Name *</Label>
                                <Input value={tournamentForm.name} onChange={(e) => setTournamentForm({...tournamentForm, name: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Sport *</Label>
                                <Select value={tournamentForm.sport} onValueChange={(v) => setTournamentForm({...tournamentForm, sport: v, disciplines: []})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10"><SelectValue placeholder="Select sport" /></SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {sports.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Disciplines Section - Shows when sport is selected */}
                        {tournamentForm.sport && sportDisciplines[tournamentForm.sport] && (
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    Events/Disciplines for {tournamentForm.sport} *
                                </Label>
                                <p className="text-xs text-muted-foreground mb-2">
                                    Select the events that will be included in this tournament
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 bg-[#0a1628] border border-white/10 max-h-48 overflow-y-auto">
                                    <div className="col-span-full mb-2">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm text-primary">
                                            <input
                                                type="checkbox"
                                                checked={tournamentForm.disciplines.length === sportDisciplines[tournamentForm.sport].length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTournamentForm({...tournamentForm, disciplines: [...sportDisciplines[tournamentForm.sport]]});
                                                    } else {
                                                        setTournamentForm({...tournamentForm, disciplines: []});
                                                    }
                                                }}
                                                className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                            />
                                            Select All Events
                                        </label>
                                    </div>
                                    {sportDisciplines[tournamentForm.sport].map((discipline) => (
                                        <label key={discipline} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="checkbox"
                                                checked={tournamentForm.disciplines.includes(discipline)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTournamentForm({...tournamentForm, disciplines: [...tournamentForm.disciplines, discipline]});
                                                    } else {
                                                        setTournamentForm({...tournamentForm, disciplines: tournamentForm.disciplines.filter(d => d !== discipline)});
                                                    }
                                                }}
                                                className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                            />
                                            {discipline}
                                        </label>
                                    ))}
                                </div>
                                {tournamentForm.disciplines.length > 0 && (
                                    <p className="text-xs text-primary">{tournamentForm.disciplines.length} event(s) selected</p>
                                )}
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Description</Label>
                            <Textarea value={tournamentForm.description} onChange={(e) => setTournamentForm({...tournamentForm, description: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Location *</Label>
                                <Input value={tournamentForm.location} onChange={(e) => setTournamentForm({...tournamentForm, location: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Venue</Label>
                                <Input value={tournamentForm.venue} onChange={(e) => setTournamentForm({...tournamentForm, venue: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Start Date *</Label>
                                <Input type="date" value={tournamentForm.start_date} onChange={(e) => setTournamentForm({...tournamentForm, start_date: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">End Date *</Label>
                                <Input type="date" value={tournamentForm.end_date} onChange={(e) => setTournamentForm({...tournamentForm, end_date: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Registration Deadline</Label>
                                <Input type="date" value={tournamentForm.registration_deadline} onChange={(e) => setTournamentForm({...tournamentForm, registration_deadline: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Currency *</Label>
                                <Select value={tournamentForm.currency} onValueChange={(v) => setTournamentForm({...tournamentForm, currency: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                                        {currencies.map(c => (
                                            <SelectItem key={c.code} value={c.code}>
                                                {c.symbol} {c.code} - {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Max Participants</Label>
                                <Input type="number" value={tournamentForm.max_participants} onChange={(e) => setTournamentForm({...tournamentForm, max_participants: parseInt(e.target.value)})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">
                                    {isTeamSport(tournamentForm.sport) ? 'Entry Fee per Team' : 'Base Entry Fee'} ({currencies.find(c => c.code === tournamentForm.currency)?.symbol || '$'})
                                </Label>
                                <Input type="number" value={tournamentForm.entry_fee} onChange={(e) => setTournamentForm({...tournamentForm, entry_fee: parseFloat(e.target.value)})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Early Bird Discount (%)</Label>
                                <Input type="number" value={tournamentForm.early_bird_discount} onChange={(e) => setTournamentForm({...tournamentForm, early_bird_discount: parseFloat(e.target.value)})} className="bg-[#0a1628] border-white/10" placeholder="0" />
                            </div>
                        </div>
                        
                        {/* Registration Financial Details */}
                        <div className="p-4 bg-[#0a1628] border border-white/10 space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-primary uppercase tracking-wider">Registration Financial Details</h4>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={tournamentForm.fee_per_discipline}
                                        onChange={(e) => setTournamentForm({...tournamentForm, fee_per_discipline: e.target.checked})}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                    />
                                    Fee per Number of Disciplines
                                </label>
                            </div>
                            
                            {/* Discipline-based Fee Section */}
                            {tournamentForm.fee_per_discipline && (
                                <div className="space-y-3 p-3 bg-[#0d2847] border border-primary/20">
                                    <p className="text-xs text-muted-foreground">
                                        Set registration fee based on number of disciplines athlete registers for
                                    </p>
                                    <div className="grid grid-cols-5 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground text-center block">1 Event</Label>
                                            <div className="flex items-center">
                                                <span className="text-xs text-muted-foreground mr-1">{currencies.find(c => c.code === tournamentForm.currency)?.symbol}</span>
                                                <Input 
                                                    type="number" 
                                                    value={tournamentForm.fee_1_discipline} 
                                                    onChange={(e) => setTournamentForm({...tournamentForm, fee_1_discipline: parseFloat(e.target.value) || 0})} 
                                                    className="bg-[#0a1628] border-white/10 h-9 text-center" 
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground text-center block">2 Events</Label>
                                            <div className="flex items-center">
                                                <span className="text-xs text-muted-foreground mr-1">{currencies.find(c => c.code === tournamentForm.currency)?.symbol}</span>
                                                <Input 
                                                    type="number" 
                                                    value={tournamentForm.fee_2_disciplines} 
                                                    onChange={(e) => setTournamentForm({...tournamentForm, fee_2_disciplines: parseFloat(e.target.value) || 0})} 
                                                    className="bg-[#0a1628] border-white/10 h-9 text-center" 
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground text-center block">3 Events</Label>
                                            <div className="flex items-center">
                                                <span className="text-xs text-muted-foreground mr-1">{currencies.find(c => c.code === tournamentForm.currency)?.symbol}</span>
                                                <Input 
                                                    type="number" 
                                                    value={tournamentForm.fee_3_disciplines} 
                                                    onChange={(e) => setTournamentForm({...tournamentForm, fee_3_disciplines: parseFloat(e.target.value) || 0})} 
                                                    className="bg-[#0a1628] border-white/10 h-9 text-center" 
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground text-center block">4 Events</Label>
                                            <div className="flex items-center">
                                                <span className="text-xs text-muted-foreground mr-1">{currencies.find(c => c.code === tournamentForm.currency)?.symbol}</span>
                                                <Input 
                                                    type="number" 
                                                    value={tournamentForm.fee_4_disciplines} 
                                                    onChange={(e) => setTournamentForm({...tournamentForm, fee_4_disciplines: parseFloat(e.target.value) || 0})} 
                                                    className="bg-[#0a1628] border-white/10 h-9 text-center" 
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground text-center block">5+ Events</Label>
                                            <div className="flex items-center">
                                                <span className="text-xs text-muted-foreground mr-1">{currencies.find(c => c.code === tournamentForm.currency)?.symbol}</span>
                                                <Input 
                                                    type="number" 
                                                    value={tournamentForm.fee_5_disciplines} 
                                                    onChange={(e) => setTournamentForm({...tournamentForm, fee_5_disciplines: parseFloat(e.target.value) || 0})} 
                                                    className="bg-[#0a1628] border-white/10 h-9 text-center" 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Early Bird Deadline</Label>
                                    <Input type="date" value={tournamentForm.early_bird_deadline} onChange={(e) => setTournamentForm({...tournamentForm, early_bird_deadline: e.target.value})} className="bg-[#0d2847] border-white/10" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Registration Includes</Label>
                                    <Input 
                                        value={tournamentForm.registration_includes} 
                                        onChange={(e) => setTournamentForm({...tournamentForm, registration_includes: e.target.value})} 
                                        className="bg-[#0d2847] border-white/10" 
                                        placeholder="e.g., T-shirt, meals, certificate"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Accepted Payment Methods</Label>
                                <div className="flex flex-wrap gap-3">
                                    {paymentMethods.map((method) => (
                                        <label key={method} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="checkbox"
                                                checked={tournamentForm.accepted_payment_methods.includes(method)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTournamentForm({...tournamentForm, accepted_payment_methods: [...tournamentForm.accepted_payment_methods, method]});
                                                    } else {
                                                        setTournamentForm({...tournamentForm, accepted_payment_methods: tournamentForm.accepted_payment_methods.filter(m => m !== method)});
                                                    }
                                                }}
                                                className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                            />
                                            {method}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        {/* Prize Money Section */}
                        <div className="p-4 bg-[#0a1628] border border-white/10 space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-accent uppercase tracking-wider">
                                    Prize Money ({tournamentForm.currency})
                                </h4>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={tournamentForm.prize_per_category}
                                        onChange={(e) => setTournamentForm({...tournamentForm, prize_per_category: e.target.checked})}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                    />
                                    Prize per Age Category
                                </label>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        Total Prize Pool ({currencies.find(c => c.code === tournamentForm.currency)?.symbol || '$'})
                                    </Label>
                                    <Input type="number" value={tournamentForm.prize_pool} onChange={(e) => setTournamentForm({...tournamentForm, prize_pool: parseFloat(e.target.value)})} className="bg-[#0d2847] border-white/10" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <span className="text-yellow-400">🥇</span> 1st Place ({currencies.find(c => c.code === tournamentForm.currency)?.symbol || '$'})
                                    </Label>
                                    <Input type="number" value={tournamentForm.prize_1st} onChange={(e) => setTournamentForm({...tournamentForm, prize_1st: parseFloat(e.target.value)})} className="bg-[#0d2847] border-white/10" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <span className="text-gray-300">🥈</span> 2nd Place ({currencies.find(c => c.code === tournamentForm.currency)?.symbol || '$'})
                                    </Label>
                                    <Input type="number" value={tournamentForm.prize_2nd} onChange={(e) => setTournamentForm({...tournamentForm, prize_2nd: parseFloat(e.target.value)})} className="bg-[#0d2847] border-white/10" />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                        <span className="text-orange-400">🥉</span> 3rd Place ({currencies.find(c => c.code === tournamentForm.currency)?.symbol || '$'})
                                    </Label>
                                    <Input type="number" value={tournamentForm.prize_3rd} onChange={(e) => setTournamentForm({...tournamentForm, prize_3rd: parseFloat(e.target.value)})} className="bg-[#0d2847] border-white/10" />
                                </div>
                            </div>
                            
                            {tournamentForm.prize_per_category && tournamentForm.age_categories.length > 0 && (
                                <p className="text-xs text-accent">
                                    Prize money will be awarded for each of the {tournamentForm.age_categories.length} age categories selected
                                </p>
                            )}
                        </div>
                        
                        {/* Game Settings */}
                        {tournamentForm.sport && (
                        <div className="p-4 bg-[#0a1628] border border-white/10 space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-primary uppercase tracking-wider">Game Settings</h4>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-white/20 text-xs"
                                    onClick={() => {
                                        const settings = getSportSettings(tournamentForm.sport);
                                        setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                period_duration: settings.defaultDuration,
                                                num_periods: settings.defaultPeriods,
                                                break_duration: settings.defaultBreak,
                                                extra_time_enabled: settings.hasExtraTime,
                                                extra_time_duration: settings.extraTimeDuration || 0,
                                                extra_time_periods: settings.extraTimePeriods || 2
                                            }
                                        });
                                    }}
                                >
                                    Load Defaults
                                </Button>
                            </div>
                            
                            <p className="text-xs text-muted-foreground">
                                Customize game duration and rules for {tournamentForm.sport}
                            </p>

                            {/* Time-based sports settings */}
                            {getSportSettings(tournamentForm.sport).defaultDuration > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        {getSportSettings(tournamentForm.sport).periodName} Duration (min)
                                    </Label>
                                    <Input
                                        type="number"
                                        value={tournamentForm.game_settings?.period_duration || 0}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                period_duration: parseFloat(e.target.value) || 0
                                            }
                                        })}
                                        className="bg-[#0d2847] border-white/10"
                                        placeholder={`e.g., ${getSportSettings(tournamentForm.sport).defaultDuration}`}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        Number of {getSportSettings(tournamentForm.sport).periodName}s
                                    </Label>
                                    <Input
                                        type="number"
                                        value={tournamentForm.game_settings?.num_periods || 2}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                num_periods: parseInt(e.target.value) || 2
                                            }
                                        })}
                                        className="bg-[#0d2847] border-white/10"
                                        min="1"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Break/Half-Time (min)</Label>
                                    <Input
                                        type="number"
                                        value={tournamentForm.game_settings?.break_duration || 0}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                break_duration: parseFloat(e.target.value) || 0
                                            }
                                        })}
                                        className="bg-[#0d2847] border-white/10"
                                    />
                                </div>
                            </div>
                            )}

                            {/* Points-based sports info */}
                            {getSportSettings(tournamentForm.sport).pointsToWin && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Points to Win</Label>
                                    <Input
                                        type="number"
                                        value={getSportSettings(tournamentForm.sport).pointsToWin}
                                        className="bg-[#0d2847] border-white/10"
                                        disabled
                                    />
                                    <p className="text-xs text-muted-foreground">Standard rules apply</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">
                                        Best of ({getSportSettings(tournamentForm.sport).periodName}s)
                                    </Label>
                                    <Input
                                        type="number"
                                        value={tournamentForm.game_settings?.num_periods || getSportSettings(tournamentForm.sport).defaultPeriods}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                num_periods: parseInt(e.target.value) || 3
                                            }
                                        })}
                                        className="bg-[#0d2847] border-white/10"
                                        min="1"
                                    />
                                </div>
                            </div>
                            )}

                            {/* Chess time control */}
                            {getSportSettings(tournamentForm.sport).hasTimeControl && (
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Time Control</Label>
                                <Select
                                    value={tournamentForm.game_settings?.time_control || ''}
                                    onValueChange={(v) => setTournamentForm({
                                        ...tournamentForm,
                                        game_settings: {
                                            ...tournamentForm.game_settings,
                                            time_control: v
                                        }
                                    })}
                                >
                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                        <SelectValue placeholder="Select time control" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {getSportSettings(tournamentForm.sport).timeControls?.map((tc) => (
                                            <SelectItem key={tc} value={tc}>{tc}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            )}

                            {/* Tennis/Padel Specific Settings */}
                            {getSportSettings(tournamentForm.sport).isRacquetSport && (
                            <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
                                <h4 className="text-sm font-semibold text-primary flex items-center gap-2">
                                    <Trophy className="w-4 h-4" />
                                    {tournamentForm.sport} Match Rules
                                </h4>
                                
                                {/* Default Rules Toggle */}
                                <div className="flex items-center gap-3 mb-4">
                                    <input
                                        type="checkbox"
                                        checked={tournamentForm.game_settings?.use_default_rules ?? true}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                use_default_rules: e.target.checked
                                            }
                                        })}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                    />
                                    <Label className="text-sm cursor-pointer">Use Default Rules (ITF/WPT Standard)</Label>
                                </div>

                                {!tournamentForm.game_settings?.use_default_rules && (
                                <div className="space-y-4 pl-2 border-l-2 border-primary/30">
                                    {/* Match Format */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Match Format</Label>
                                            <Select
                                                value={tournamentForm.game_settings?.match_format || 'best_of_3'}
                                                onValueChange={(v) => setTournamentForm({
                                                    ...tournamentForm,
                                                    game_settings: {
                                                        ...tournamentForm.game_settings,
                                                        match_format: v
                                                    }
                                                })}
                                            >
                                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0d2847] border-white/10">
                                                    <SelectItem value="best_of_1">Best of 1 Set</SelectItem>
                                                    <SelectItem value="best_of_2">Best of 2 Sets</SelectItem>
                                                    <SelectItem value="best_of_3">Best of 3 Sets</SelectItem>
                                                    <SelectItem value="best_of_5">Best of 5 Sets</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Games Per Set</Label>
                                            <Select
                                                value={String(tournamentForm.game_settings?.games_per_set || 6)}
                                                onValueChange={(v) => setTournamentForm({
                                                    ...tournamentForm,
                                                    game_settings: {
                                                        ...tournamentForm.game_settings,
                                                        games_per_set: parseInt(v)
                                                    }
                                                })}
                                            >
                                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0d2847] border-white/10">
                                                    <SelectItem value="4">4 Games (Short Set)</SelectItem>
                                                    <SelectItem value="6">6 Games (Standard)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Deuce Rules */}
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground">Deuce Rule (At 40-40)</Label>
                                        <Select
                                            value={tournamentForm.game_settings?.deuce_rule || 'standard'}
                                            onValueChange={(v) => setTournamentForm({
                                                ...tournamentForm,
                                                game_settings: {
                                                    ...tournamentForm.game_settings,
                                                    deuce_rule: v
                                                }
                                            })}
                                        >
                                            <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#0d2847] border-white/10">
                                                <SelectItem value="standard">Standard Deuce (Advantage)</SelectItem>
                                                <SelectItem value="golden_point">Straight Golden Point (No-Ad)</SelectItem>
                                                <SelectItem value="two_deuce_golden">2 Deuces then Golden Point</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {tournamentForm.game_settings?.deuce_rule === 'standard' && 'Must win by 2 points after deuce'}
                                            {tournamentForm.game_settings?.deuce_rule === 'golden_point' && 'Single sudden-death point at deuce'}
                                            {tournamentForm.game_settings?.deuce_rule === 'two_deuce_golden' && 'Play 2 deuces, then golden point decides'}
                                        </p>
                                    </div>

                                    {/* Tiebreaker Settings */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Tiebreaker Points</Label>
                                            <Select
                                                value={String(tournamentForm.game_settings?.tiebreaker_points || 7)}
                                                onValueChange={(v) => setTournamentForm({
                                                    ...tournamentForm,
                                                    game_settings: {
                                                        ...tournamentForm.game_settings,
                                                        tiebreaker_points: parseInt(v)
                                                    }
                                                })}
                                            >
                                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0d2847] border-white/10">
                                                    <SelectItem value="7">7 Points (Standard)</SelectItem>
                                                    <SelectItem value="10">10 Points (Super Tiebreaker)</SelectItem>
                                                    <SelectItem value="12">12 Points (Championship)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Single Set Tiebreaker Option */}
                                        {tournamentForm.game_settings?.match_format === 'best_of_1' && (
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground">Single Set Format</Label>
                                            <Select
                                                value={tournamentForm.game_settings?.single_set_tiebreaker || 'super_tiebreaker'}
                                                onValueChange={(v) => setTournamentForm({
                                                    ...tournamentForm,
                                                    game_settings: {
                                                        ...tournamentForm.game_settings,
                                                        single_set_tiebreaker: v
                                                    }
                                                })}
                                            >
                                                <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-[#0d2847] border-white/10">
                                                    <SelectItem value="standard">Standard Set (with tiebreak at 6-6)</SelectItem>
                                                    <SelectItem value="super_tiebreaker">Super Tiebreaker (10 points)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        )}
                                    </div>

                                    {/* Summary of Rules */}
                                    <div className="p-3 bg-primary/10 border border-primary/20 mt-2">
                                        <p className="text-xs font-semibold text-primary mb-2">Match Rules Summary:</p>
                                        <ul className="text-xs text-muted-foreground space-y-1">
                                            <li>• Format: {
                                                tournamentForm.game_settings?.match_format === 'best_of_1' ? 'Best of 1 Set' :
                                                tournamentForm.game_settings?.match_format === 'best_of_2' ? 'Best of 2 Sets' :
                                                tournamentForm.game_settings?.match_format === 'best_of_5' ? 'Best of 5 Sets' :
                                                'Best of 3 Sets'
                                            }</li>
                                            <li>• Games per Set: {tournamentForm.game_settings?.games_per_set || 6}</li>
                                            <li>• Deuce: {
                                                tournamentForm.game_settings?.deuce_rule === 'golden_point' ? 'Golden Point (No-Ad)' :
                                                tournamentForm.game_settings?.deuce_rule === 'two_deuce_golden' ? '2 Deuces then Golden Point' :
                                                'Standard Advantage'
                                            }</li>
                                            <li>• Tiebreaker: First to {tournamentForm.game_settings?.tiebreaker_points || 7} points (win by 2)</li>
                                            {tournamentForm.game_settings?.match_format === 'best_of_1' && (
                                                <li>• Single Set: {
                                                    tournamentForm.game_settings?.single_set_tiebreaker === 'super_tiebreaker' 
                                                        ? 'Super Tiebreaker (10 points)'
                                                        : 'Standard with Tiebreak'
                                                }</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                )}

                                {tournamentForm.game_settings?.use_default_rules && (
                                <div className="p-3 bg-cyan-900/20 border border-cyan-500/30">
                                    <p className="text-xs text-cyan-400 font-semibold mb-2">Default {tournamentForm.sport} Rules:</p>
                                    <ul className="text-xs text-muted-foreground space-y-1">
                                        <li>• Best of 3 Sets (Finals: Best of 5 optional)</li>
                                        <li>• 6 Games per Set</li>
                                        <li>• {tournamentForm.sport === 'Padel' ? 'Golden Point (No-Ad)' : 'Standard Deuce (Advantage)'}</li>
                                        <li>• Tiebreaker at 6-6 (First to 7, win by 2)</li>
                                    </ul>
                                </div>
                                )}
                            </div>
                            )}

                            {/* Extra Time Settings */}
                            {getSportSettings(tournamentForm.sport).hasExtraTime && (
                            <div className="border-t border-white/10 pt-4 mt-4">
                                <div className="flex items-center gap-3 mb-4">
                                    <input
                                        type="checkbox"
                                        checked={tournamentForm.game_settings?.extra_time_enabled || false}
                                        onChange={(e) => setTournamentForm({
                                            ...tournamentForm,
                                            game_settings: {
                                                ...tournamentForm.game_settings,
                                                extra_time_enabled: e.target.checked
                                            }
                                        })}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                    />
                                    <Label className="text-sm cursor-pointer">Enable Extra Time / Overtime</Label>
                                </div>
                                
                                {tournamentForm.game_settings?.extra_time_enabled && (
                                <div className="grid grid-cols-2 gap-4 pl-6">
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground">Extra Time Duration (min)</Label>
                                        <Input
                                            type="number"
                                            value={tournamentForm.game_settings?.extra_time_duration || 0}
                                            onChange={(e) => setTournamentForm({
                                                ...tournamentForm,
                                                game_settings: {
                                                    ...tournamentForm.game_settings,
                                                    extra_time_duration: parseFloat(e.target.value) || 0
                                                }
                                            })}
                                            className="bg-[#0d2847] border-white/10"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground">Extra Time Periods</Label>
                                        <Input
                                            type="number"
                                            value={tournamentForm.game_settings?.extra_time_periods || 2}
                                            onChange={(e) => setTournamentForm({
                                                ...tournamentForm,
                                                game_settings: {
                                                    ...tournamentForm.game_settings,
                                                    extra_time_periods: parseInt(e.target.value) || 2
                                                }
                                            })}
                                            className="bg-[#0d2847] border-white/10"
                                            min="1"
                                        />
                                    </div>
                                </div>
                                )}
                            </div>
                            )}

                            {/* Custom Rules */}
                            <div className="space-y-2 pt-2">
                                <Label className="text-xs text-muted-foreground">Custom Rules / Notes (Optional)</Label>
                                <Textarea
                                    value={tournamentForm.game_settings?.custom_rules || ''}
                                    onChange={(e) => setTournamentForm({
                                        ...tournamentForm,
                                        game_settings: {
                                            ...tournamentForm.game_settings,
                                            custom_rules: e.target.value
                                        }
                                    })}
                                    className="bg-[#0d2847] border-white/10"
                                    placeholder="Enter any custom rules or modifications for this tournament..."
                                    rows={3}
                                />
                            </div>

                            {/* Game Duration Summary */}
                            {tournamentForm.game_settings?.period_duration > 0 && (
                            <div className="p-3 bg-primary/10 border border-primary/20 mt-4">
                                <p className="text-xs text-primary">
                                    Total Game Time: {((tournamentForm.game_settings?.period_duration || 0) * (tournamentForm.game_settings?.num_periods || 2)) + 
                                    (((tournamentForm.game_settings?.num_periods || 2) - 1) * (tournamentForm.game_settings?.break_duration || 0))} minutes
                                    {tournamentForm.game_settings?.extra_time_enabled && 
                                        ` (+${(tournamentForm.game_settings?.extra_time_duration || 0) * (tournamentForm.game_settings?.extra_time_periods || 2)} min extra time if needed)`
                                    }
                                </p>
                            </div>
                            )}
                        </div>
                        )}
                        
                        {/* Gender Categories Selection */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Gender Categories *</Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Select the gender categories for this tournament
                            </p>
                            <div className="flex flex-wrap gap-4 p-4 bg-[#0a1628] border border-white/10">
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-primary mr-4">
                                    <input
                                        type="checkbox"
                                        checked={tournamentForm.gender_categories.length === genderCategories.length}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setTournamentForm({...tournamentForm, gender_categories: [...genderCategories]});
                                            } else {
                                                setTournamentForm({...tournamentForm, gender_categories: []});
                                            }
                                        }}
                                        className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                    />
                                    All Genders
                                </label>
                                {genderCategories.map((gender) => (
                                    <label key={gender} className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="checkbox"
                                            checked={tournamentForm.gender_categories.includes(gender)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setTournamentForm({...tournamentForm, gender_categories: [...tournamentForm.gender_categories, gender]});
                                                } else {
                                                    setTournamentForm({...tournamentForm, gender_categories: tournamentForm.gender_categories.filter(g => g !== gender)});
                                                }
                                            }}
                                            className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                        />
                                        {gender}
                                    </label>
                                ))}
                            </div>
                            {tournamentForm.gender_categories.length > 0 && (
                                <p className="text-xs text-primary">{tournamentForm.gender_categories.length} gender category(ies) selected</p>
                            )}
                        </div>

                        {/* Age Groups Selection */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Age Groups *</Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Select the age groups eligible for this tournament
                            </p>
                            <div className="p-4 bg-[#0a1628] border border-white/10">
                                <div className="flex items-center gap-4 mb-3 pb-3 border-b border-white/10">
                                    <label className="flex items-center gap-2 cursor-pointer text-sm text-primary">
                                        <input
                                            type="checkbox"
                                            checked={tournamentForm.age_groups.length === ageGroups.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setTournamentForm({...tournamentForm, age_groups: [...ageGroups]});
                                                } else {
                                                    setTournamentForm({...tournamentForm, age_groups: []});
                                                }
                                            }}
                                            className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                        />
                                        Select All Age Groups
                                    </label>
                                </div>
                                <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                                    {ageGroups.map((ageGroup) => (
                                        <label key={ageGroup} className="flex items-center gap-2 cursor-pointer text-sm">
                                            <input
                                                type="checkbox"
                                                checked={tournamentForm.age_groups.includes(ageGroup)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTournamentForm({...tournamentForm, age_groups: [...tournamentForm.age_groups, ageGroup]});
                                                    } else {
                                                        setTournamentForm({...tournamentForm, age_groups: tournamentForm.age_groups.filter(a => a !== ageGroup)});
                                                    }
                                                }}
                                                className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                            />
                                            {ageGroup}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {tournamentForm.age_groups.length > 0 && (
                                <p className="text-xs text-primary">{tournamentForm.age_groups.length} age group(s) selected</p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Status</Label>
                            <Select value={tournamentForm.tournament_status} onValueChange={(v) => setTournamentForm({...tournamentForm, tournament_status: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {tournamentStatuses.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTournamentDialog(false)} className="border-white/10">Cancel</Button>
                        <Button onClick={handleSaveTournament} className="bg-primary hover:bg-cyan-400 text-[#0a1628]">
                            {editingTournament ? 'Update' : 'Create'} Tournament
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Athlete Dialog */}
            <Dialog open={showAthleteDialog} onOpenChange={setShowAthleteDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            {editingAthlete ? 'Edit Athlete' : 'Create Athlete'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Full Name *</Label>
                                <Input value={athleteForm.full_name} onChange={(e) => setAthleteForm({...athleteForm, full_name: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                                <Input type="date" value={athleteForm.date_of_birth} onChange={(e) => setAthleteForm({...athleteForm, date_of_birth: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Age Category *</Label>
                                <Select value={athleteForm.age_category} onValueChange={(v) => setAthleteForm({...athleteForm, age_category: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10"><SelectValue placeholder="Select category" /></SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {ageCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Country</Label>
                                <Input value={athleteForm.country} onChange={(e) => setAthleteForm({...athleteForm, country: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">City</Label>
                                <Input value={athleteForm.city} onChange={(e) => setAthleteForm({...athleteForm, city: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">School</Label>
                                <Input value={athleteForm.school} onChange={(e) => setAthleteForm({...athleteForm, school: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Sports (comma-separated)</Label>
                            <Input 
                                value={athleteForm.sports?.join(', ') || ''} 
                                onChange={(e) => setAthleteForm({...athleteForm, sports: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} 
                                className="bg-[#0a1628] border-white/10"
                                placeholder="Football, Basketball, Tennis"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Mobile Number</Label>
                            <Input value={athleteForm.mobile_number} onChange={(e) => setAthleteForm({...athleteForm, mobile_number: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Bio</Label>
                            <Textarea value={athleteForm.bio} onChange={(e) => setAthleteForm({...athleteForm, bio: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                        
                        {/* Manual Location Entry for Admin */}
                        <ManualLocationEntry
                            location={athleteForm.profile_location}
                            onLocationChange={(loc) => setAthleteForm({...athleteForm, profile_location: loc})}
                        />
                        
                        <div className="flex items-center gap-2">
                            <input type="checkbox" checked={athleteForm.verified} onChange={(e) => setAthleteForm({...athleteForm, verified: e.target.checked})} className="w-4 h-4" />
                            <Label className="text-sm">Mark as Verified</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAthleteDialog(false)} className="border-white/10">Cancel</Button>
                        <Button onClick={handleSaveAthlete} className="bg-primary hover:bg-cyan-400 text-[#0a1628]">
                            {editingAthlete ? 'Update' : 'Create'} Athlete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* School Dialog */}
            <Dialog open={showSchoolDialog} onOpenChange={setShowSchoolDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            {editingSchool ? 'Edit School' : 'Add School'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">School Name *</Label>
                                <Input value={schoolForm.school_name} onChange={(e) => setSchoolForm({...schoolForm, school_name: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="school-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Sports * (Select multiple)</Label>
                                <div className="bg-[#0a1628] border border-white/10 rounded p-3 max-h-40 overflow-y-auto" data-testid="school-sports-select">
                                    {/* Selected sports badges */}
                                    {schoolForm.sports.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-white/5">
                                            {schoolForm.sports.map(sport => (
                                                <span 
                                                    key={sport} 
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded"
                                                >
                                                    {sport}
                                                    <button 
                                                        type="button"
                                                        onClick={() => setSchoolForm({
                                                            ...schoolForm, 
                                                            sports: schoolForm.sports.filter(s => s !== sport)
                                                        })}
                                                        className="hover:text-red-400 ml-1"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* Sports checkboxes */}
                                    <div className="grid grid-cols-2 gap-2">
                                        {sports.map(s => (
                                            <label 
                                                key={s} 
                                                className="flex items-center gap-2 text-sm cursor-pointer hover:text-primary"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={schoolForm.sports.includes(s)}
                                                    onChange={(e) => handleSportToggle(s, e.target.checked)}
                                                    className="w-4 h-4 rounded border-white/20 bg-[#0a1628] text-primary focus:ring-primary"
                                                    data-testid={`sport-checkbox-${s.toLowerCase().replace(/\s+/g, '-')}`}
                                                />
                                                {s}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {schoolForm.sports.length === 0 && (
                                    <p className="text-xs text-red-400">Please select at least one sport</p>
                                )}
                            </div>
                        </div>
                        
                        {/* Sport-Based Rankings Section */}
                        {schoolForm.sports.length > 0 && (
                            <div className="border-t border-white/10 pt-4 mt-2">
                                <h3 className="text-sm font-semibold text-accent mb-4 flex items-center gap-2">
                                    <Trophy className="w-4 h-4" />
                                    Sport Rankings (Per Sport)
                                </h3>
                                <div className="space-y-4 max-h-60 overflow-y-auto">
                                    {schoolForm.sports.map(sport => (
                                        <div key={sport} className="bg-[#0a1628] p-3 rounded border border-white/5">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded">{sport}</span>
                                            </div>
                                            <div className="grid grid-cols-4 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Rank</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={schoolForm.sport_rankings[sport]?.rank || 999}
                                                        onChange={(e) => updateSportRanking(sport, 'rank', parseInt(e.target.value) || 999)}
                                                        className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                        data-testid={`sport-rank-${sport.toLowerCase().replace(/\s+/g, '-')}`}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Points</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={schoolForm.sport_rankings[sport]?.points || 0}
                                                        onChange={(e) => updateSportRanking(sport, 'points', parseFloat(e.target.value) || 0)}
                                                        className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Wins</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={schoolForm.sport_rankings[sport]?.wins || 0}
                                                        onChange={(e) => updateSportRanking(sport, 'wins', parseInt(e.target.value) || 0)}
                                                        className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-muted-foreground">Losses</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={schoolForm.sport_rankings[sport]?.losses || 0}
                                                        onChange={(e) => updateSportRanking(sport, 'losses', parseInt(e.target.value) || 0)}
                                                        className="bg-[#0d2847] border-white/10 h-8 text-sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Country</Label>
                                <Input value={schoolForm.country} onChange={(e) => setSchoolForm({...schoolForm, country: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">City</Label>
                                <Input value={schoolForm.city} onChange={(e) => setSchoolForm({...schoolForm, city: e.target.value})} className="bg-[#0a1628] border-white/10" />
                            </div>
                        </div>
                        
                        {/* Institution Type */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Institution Type *</Label>
                            <Select value={schoolForm.institution_type} onValueChange={(v) => setSchoolForm({...schoolForm, institution_type: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="institution-type-select">
                                    <SelectValue placeholder="Select institution type" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    <SelectItem value="international_school">International School</SelectItem>
                                    <SelectItem value="private_school">Private School</SelectItem>
                                    <SelectItem value="government_public_school">Government / Public School</SelectItem>
                                    <SelectItem value="sports_academy">Sports Academy</SelectItem>
                                    <SelectItem value="community_sports_club">Community Sports Club</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Athletes Count (Overall) */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Total Athletes Count</Label>
                                <Input 
                                    type="number" 
                                    value={schoolForm.athletes_count} 
                                    onChange={(e) => setSchoolForm({...schoolForm, athletes_count: parseInt(e.target.value) || 0})} 
                                    className="bg-[#0a1628] border-white/10"
                                    placeholder="Total athletes across all sports"
                                />
                            </div>
                        </div>
                        
                        {/* Fee Waiver Section */}
                        <div className="border-t border-white/10 pt-4 mt-4">
                            <h3 className="text-sm font-semibold text-primary mb-4 flex items-center gap-2">
                                <DollarSign className="w-4 h-4" />
                                Registration Fee Waiver (Financial Aid)
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground">Waiver Type</Label>
                                    <Select 
                                        value={schoolForm.fee_waiver_type} 
                                        onValueChange={(v) => setSchoolForm({
                                            ...schoolForm, 
                                            fee_waiver_type: v,
                                            fee_waiver_amount: v === 'none' ? 0 : schoolForm.fee_waiver_amount
                                        })}
                                    >
                                        <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="fee-waiver-type-select">
                                            <SelectValue placeholder="Select waiver type" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            <SelectItem value="none">No Waiver (Full Fee)</SelectItem>
                                            <SelectItem value="partial">Part Waiver (Discount)</SelectItem>
                                            <SelectItem value="full">Full Waiver (100% Free)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {schoolForm.fee_waiver_type === 'partial' && (
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground">Discount Amount (KSH)</Label>
                                        <Input 
                                            type="number" 
                                            value={schoolForm.fee_waiver_amount} 
                                            onChange={(e) => setSchoolForm({...schoolForm, fee_waiver_amount: parseFloat(e.target.value) || 0})} 
                                            className="bg-[#0a1628] border-white/10" 
                                            placeholder="e.g., 100, 500"
                                            data-testid="fee-waiver-amount-input"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {schoolForm.fee_waiver_type !== 'none' && (
                                <div className="space-y-2 mt-4">
                                    <Label className="text-xs text-muted-foreground">Reason for Waiver (Optional)</Label>
                                    <Input 
                                        value={schoolForm.fee_waiver_reason} 
                                        onChange={(e) => setSchoolForm({...schoolForm, fee_waiver_reason: e.target.value})} 
                                        className="bg-[#0a1628] border-white/10" 
                                        placeholder="e.g., Sponsor Program, Government Scholarship"
                                        data-testid="fee-waiver-reason-input"
                                    />
                                </div>
                            )}
                            
                            {/* Waiver Summary */}
                            {schoolForm.fee_waiver_type !== 'none' && (
                                <div className="mt-4 p-3 bg-green-900/20 border border-green-500/30 rounded">
                                    <p className="text-xs text-green-400 font-semibold">
                                        {schoolForm.fee_waiver_type === 'full' 
                                            ? '✓ Athletes from this school will receive 100% fee waiver'
                                            : `✓ Athletes from this school will receive KSH ${schoolForm.fee_waiver_amount || 0} discount`
                                        }
                                        {schoolForm.fee_waiver_reason && ` (${schoolForm.fee_waiver_reason})`}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSchoolDialog(false)} className="border-white/10">Cancel</Button>
                        <Button onClick={handleSaveSchool} className="bg-primary hover:bg-cyan-400 text-[#0a1628]" data-testid="save-school-btn">
                            {editingSchool ? 'Update' : 'Add'} School
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            Import {importType === 'athletes' ? 'Athletes' : 'Schools'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-6">
                        <div className="p-8 border-2 border-dashed border-white/20 text-center">
                            <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground mb-2">Upload a CSV, Excel, or JSON file</p>
                            <p className="text-xs text-muted-foreground mb-4">Supported formats: .csv, .xlsx, .xls, .json</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls,.json"
                                onChange={handleFileUpload}
                                className="hidden"
                                data-testid="import-file-input"
                            />
                            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-white/10" data-testid="select-file-btn">
                                Select File
                            </Button>
                        </div>
                        <div className="mt-4 p-4 bg-[#0a1628] border border-white/10">
                            <p className="text-xs text-muted-foreground mb-2">
                                {importType === 'athletes' ? 'Expected columns:' : 'Expected columns:'}
                            </p>
                            <code className="text-xs text-primary">
                                {importType === 'athletes' 
                                    ? 'full_name, age_category, country, city, school, sports (semicolon-separated), verified'
                                    : 'school_name, country, city, sport, rank, points, wins, losses'
                                }
                            </code>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Role Dialog */}
            <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            Change User Role
                        </DialogTitle>
                    </DialogHeader>
                    {editingUserRole && (
                        <div className="py-4">
                            <p className="text-muted-foreground mb-4">
                                Change role for <span className="text-white font-medium">{editingUserRole.full_name}</span>
                            </p>
                            <p className="text-sm text-muted-foreground mb-4">
                                Current role: <span className="text-primary uppercase">{editingUserRole.role}</span>
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {userRoles.map(role => (
                                    <Button
                                        key={role}
                                        onClick={() => handleUpdateUserRole(role)}
                                        variant={editingUserRole.role === role ? "default" : "outline"}
                                        className={editingUserRole.role === role ? "bg-primary text-[#0a1628]" : "border-white/10"}
                                        disabled={editingUserRole.id === user?.id && role !== 'admin'}
                                    >
                                        {role}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Bulk Role Change Dialog */}
            <Dialog open={showBulkRoleDialog} onOpenChange={setShowBulkRoleDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase">
                            Bulk Change Role
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-muted-foreground mb-4">
                            Change role for <span className="text-white font-medium">{selectedUsers.length} selected user(s)</span>
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                            Select a new role to apply to all selected users:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                            {userRoles.map(role => (
                                <Button
                                    key={role}
                                    onClick={() => handleBulkRoleChange(role)}
                                    variant="outline"
                                    className="border-white/10 hover:bg-primary hover:text-[#0a1628] hover:border-primary"
                                    data-testid={`bulk-role-${role}`}
                                >
                                    {role}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkRoleDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Admin Account Dialog */}
            <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <Shield className="w-5 h-5 text-accent" />
                            Create Admin Account
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Full Name *</Label>
                            <Input 
                                value={newAdminForm.full_name} 
                                onChange={(e) => setNewAdminForm({...newAdminForm, full_name: e.target.value})} 
                                className="bg-[#0a1628] border-white/10" 
                                placeholder="Enter full name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Email *</Label>
                            <Input 
                                type="email"
                                value={newAdminForm.email} 
                                onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})} 
                                className="bg-[#0a1628] border-white/10" 
                                placeholder="admin@example.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Password *</Label>
                            <Input 
                                type="password"
                                value={newAdminForm.password} 
                                onChange={(e) => setNewAdminForm({...newAdminForm, password: e.target.value})} 
                                className="bg-[#0a1628] border-white/10" 
                                placeholder="Min 8 characters"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Admin Role *</Label>
                            <Select value={newAdminForm.admin_role} onValueChange={(v) => setNewAdminForm({...newAdminForm, admin_role: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {adminRoles.map(role => (
                                        <SelectItem key={role.role} value={role.role}>
                                            <div>
                                                <span className="font-medium">{role.name}</span>
                                                <span className="text-xs text-muted-foreground ml-2">- {role.description}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="p-3 bg-[#0a1628] border border-white/10 text-xs text-muted-foreground">
                            <p className="font-bold text-white mb-2">Role Permissions:</p>
                            <ul className="space-y-1">
                                <li><span className="text-accent">Chairman:</span> Full access, can create roles & accounts</li>
                                <li><span className="text-primary">Director:</span> All access except create tournament, no import/export</li>
                                <li><span className="text-success">Admin Team:</span> All access except create tournament, can import/export</li>
                                <li><span className="text-orange-400">Official/Referee:</span> View tournaments, update scores & MVP</li>
                                <li><span className="text-purple-400">Account Manager:</span> All access except create tournament, view all accounts</li>
                            </ul>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdminDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button onClick={handleCreateAdminAccount} className="bg-accent hover:bg-yellow-400 text-black">
                            Create Account
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;
